import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttachmentStore } from 'stores/attachmentStore';
import { SalesOrderAttachmentMeta } from 'types/generatedSchemaTypes';
import {
  mapAttachmentsToFiles,
  revokeAttachmentUri,
} from 'utils/attachmentUtils';

type OrderAttachment = Pick<
  SalesOrderAttachmentMeta,
  'uid' | 'fileName' | 'mimeType' | 'sizeBytes'
>;

type AttachmentHydrationMode = 'hydrate' | 'observe';

type UseHydrateOrderAttachmentsArgs = {
  orderUid: string | undefined | null;
  orderAttachments: OrderAttachment[] | null | undefined;
  mode: AttachmentHydrationMode;
};

// Active hydrator count per orderUid, last unmount drops the entries.
const hydrationRefCounts = new Map<string, number>();

export const __resetHydrationRefCountsForTests = (): void => {
  hydrationRefCounts.clear();
};

export const useHydrateOrderAttachments = ({
  orderUid,
  orderAttachments,
  mode,
}: UseHydrateOrderAttachmentsArgs): void => {
  const { t } = useTranslation();

  const attachmentsSignature = useMemo(
    () =>
      (orderAttachments ?? [])
        .map((a) => a?.uid)
        .filter(Boolean)
        .join(','),
    [orderAttachments]
  );

  const fallbackAttachmentName = t('longTermParking.attachment.fallbackName');

  const orderAttachmentsRef = useRef(orderAttachments);
  orderAttachmentsRef.current = orderAttachments;

  // Lifecycle: last unmount drops every entry for the order (pending included).
  useEffect(() => {
    if (!orderUid || mode !== 'hydrate') return;
    hydrationRefCounts.set(
      orderUid,
      (hydrationRefCounts.get(orderUid) ?? 0) + 1
    );
    return () => {
      const next = (hydrationRefCounts.get(orderUid) ?? 1) - 1;
      if (next > 0) {
        hydrationRefCounts.set(orderUid, next);
        return;
      }
      hydrationRefCounts.delete(orderUid);
      const { attachments: current, setAttachments } =
        useAttachmentStore.getState();
      current.forEach((a) => {
        if (a.contextId === orderUid && a.uri) revokeAttachmentUri(a.uri);
      });
      setAttachments(current.filter((a) => a.contextId !== orderUid));
    };
  }, [orderUid, mode]);

  // Data sync on signature change, pending entries are superseded by matching uploadUid.
  useEffect(() => {
    if (!orderUid || mode !== 'hydrate') return;
    const mapped = mapAttachmentsToFiles(
      orderAttachmentsRef.current,
      orderUid,
      fallbackAttachmentName
    );
    const mappedIds = new Set(mapped.map((a) => a.id));
    const incomingUploadUids = new Set(
      mapped.map((a) => a.uploadUid).filter((uid): uid is string => !!uid)
    );

    const { attachments: current, setAttachments } =
      useAttachmentStore.getState();
    current.forEach((entry) => {
      if (entry.contextId !== orderUid) return;
      if (mappedIds.has(entry.id)) return;
      if (entry.uploadUid && incomingUploadUids.has(entry.uploadUid)) {
        revokeAttachmentUri(entry.uri);
      }
    });

    const retained = current.filter((a) => {
      if (a.contextId !== orderUid) return true;
      if (mappedIds.has(a.id)) return false;
      if (a.uploadUid && incomingUploadUids.has(a.uploadUid)) return false;
      return true;
    });

    setAttachments([...retained, ...mapped]);
  }, [orderUid, attachmentsSignature, fallbackAttachmentName, mode]);
};
